/**
 * Service de cache intelligent
 * PHASE 2 REFACTORING: Cache optimisé pour les données de campagne
 * TTL adaptatif, invalidation intelligente, métriques de performance
 */

import { logger } from '@/utils/productionLogger';

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
}

interface CacheMetrics {
  hits: number;
  misses: number;
  sets: number;
  invalidations: number;
  totalSize: number;
  hitRate: number;
}

export class CacheService {
  private static readonly CACHE_PREFIX = 'getproof_cache_';
  private static readonly METRICS_KEY = 'getproof_cache_metrics';
  
  private static getMetrics(): CacheMetrics {
    if (typeof window === 'undefined') return {
      hits: 0, misses: 0, sets: 0, invalidations: 0, totalSize: 0, hitRate: 0
    };
    
    try {
      const stored = localStorage.getItem(this.METRICS_KEY);
      return stored ? JSON.parse(stored) : {
        hits: 0, misses: 0, sets: 0, invalidations: 0, totalSize: 0, hitRate: 0
      };
    } catch {
      return { hits: 0, misses: 0, sets: 0, invalidations: 0, totalSize: 0, hitRate: 0 };
    }
  }
  
  private static setMetrics(metrics: CacheMetrics): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(this.METRICS_KEY, JSON.stringify(metrics));
    } catch {
      // Ignore localStorage errors
    }
  }

  // Configuration du cache
  private static readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly MAX_CACHE_SIZE = 1000; // Nombre max d'entrées
  
  // TTL adaptatifs par type de données
  private static readonly TTL_CONFIG = {
    'campaign_data': 5 * 60 * 1000,      // 5 minutes - données de campagne
    'referent_data': 3 * 60 * 1000,      // 3 minutes - données de référent
    'validation': 1 * 60 * 1000,         // 1 minute - validations de token
    'user_profile': 10 * 60 * 1000,      // 10 minutes - profils utilisateur
    'static_data': 30 * 60 * 1000        // 30 minutes - données statiques
  };

  /**
   * Récupère une valeur du cache
   */
  static get<T>(key: string): T | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const stored = localStorage.getItem(this.CACHE_PREFIX + key);
      if (!stored) {
        const metrics = this.getMetrics();
        metrics.misses++;
        this.updateHitRate(metrics);
        this.setMetrics(metrics);
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(stored);
      const now = Date.now();
      
      // Vérifier si l'entrée a expiré
      if (now - entry.timestamp > entry.ttl) {
        localStorage.removeItem(this.CACHE_PREFIX + key);
        const metrics = this.getMetrics();
        metrics.misses++;
        this.updateHitRate(metrics);
        this.setMetrics(metrics);
        return null;
      }

      // Mettre à jour les statistiques d'accès
      entry.accessCount++;
      entry.lastAccessed = now;
      localStorage.setItem(this.CACHE_PREFIX + key, JSON.stringify(entry));
      
      const metrics = this.getMetrics();
      metrics.hits++;
      this.updateHitRate(metrics);
      this.setMetrics(metrics);
      
      logger.debug('Cache hit', { 
        key: this.sanitizeKey(key), 
        accessCount: entry.accessCount,
        age: `${now - entry.timestamp}ms`
      });
      
      return entry.data;
    } catch (cacheError) {
      // En cas d'erreur localStorage, retourner null
      logger.warn('Cache get error', { key, error: cacheError });
      return null;
    }
  }

  /**
   * Stocke une valeur dans le cache
   */
  static set<T>(key: string, data: T, customTtl?: number): void {
    if (typeof window === 'undefined') return;
    
    try {
      const now = Date.now();
      const ttl = customTtl || this.getTtlForKey(key);
      
      // Vérifier la taille du cache et nettoyer si nécessaire
      const cacheSize = this.getCacheSize();
      if (cacheSize >= this.MAX_CACHE_SIZE) {
        this.evictLeastRecentlyUsed();
      }

      const entry: CacheEntry<T> = {
        data,
        timestamp: now,
        ttl,
        accessCount: 0,
        lastAccessed: now
      };

      localStorage.setItem(this.CACHE_PREFIX + key, JSON.stringify(entry));
      
      const metrics = this.getMetrics();
      metrics.sets++;
      metrics.totalSize = this.getCacheSize();
      this.setMetrics(metrics);
      
      logger.debug('Cache set', { 
        key: this.sanitizeKey(key), 
        ttl: `${ttl}ms`,
        cacheSize: metrics.totalSize
      });
    } catch (cacheError) {
      // En cas d'erreur localStorage (quota dépassé), nettoyer le cache
      logger.warn('Cache set error, cleaning up', { key, error: cacheError });
      this.cleanup();
    }
  }

  /**
   * Invalide une ou plusieurs entrées du cache
   */
  static invalidate(pattern: string): number {
    if (typeof window === 'undefined') return 0;
    
    let invalidatedCount = 0;
    
    try {
      if (pattern.includes('*')) {
        // Pattern matching avec wildcards
        const regex = new RegExp(pattern.replace(/\*/g, '.*'));
        const keysToRemove: string[] = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith(this.CACHE_PREFIX)) {
            const cacheKey = key.substring(this.CACHE_PREFIX.length);
            if (regex.test(cacheKey)) {
              keysToRemove.push(key);
            }
          }
        }
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          invalidatedCount++;
        });
      } else {
        // Invalidation exacte
        const fullKey = this.CACHE_PREFIX + pattern;
        if (localStorage.getItem(fullKey)) {
          localStorage.removeItem(fullKey);
          invalidatedCount = 1;
        }
      }
      
      const metrics = this.getMetrics();
      metrics.invalidations += invalidatedCount;
      metrics.totalSize = this.getCacheSize();
      this.setMetrics(metrics);
      
      logger.debug('Cache invalidation', { 
        pattern, 
        invalidatedCount,
        remainingSize: metrics.totalSize
      });
    } catch (cacheError) {
      logger.warn('Cache invalidation error', { pattern, error: cacheError });
    }
    
    return invalidatedCount;
  }

  /**
   * Invalide toutes les entrées liées à une campagne
   */
  static invalidateCampaign(campaignId: string): void {
    this.invalidate(`campaign_${campaignId}*`);
    this.invalidate(`referent_*_campaign_${campaignId}`);
  }

  /**
   * Invalide toutes les entrées liées à un référent
   */
  static invalidateReferent(referentId: string): void {
    this.invalidate(`referent_${referentId}*`);
  }

  /**
   * Vide complètement le cache
   */
  static clear(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const previousSize = this.getCacheSize();
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      const metrics = this.getMetrics();
      metrics.totalSize = 0;
      metrics.invalidations += previousSize;
      this.setMetrics(metrics);
      
      logger.info('Cache cleared', { previousSize });
    } catch (cacheError) {
      logger.warn('Cache clear error', { error: cacheError });
    }
  }

  /**
   * Retourne les métriques du cache (méthode publique)
   */
  static getCacheMetrics(): CacheMetrics {
    return this.getMetrics();
  }

  /**
   * Nettoyage automatique des entrées expirées
   */
  static cleanup(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const now = Date.now();
      let cleanedCount = 0;
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry: CacheEntry = JSON.parse(stored);
              if (now - entry.timestamp > entry.ttl) {
                keysToRemove.push(key);
              }
            }
          } catch {
            // Entrée corrompue, la supprimer
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        cleanedCount++;
      });
      
      if (cleanedCount > 0) {
        const metrics = this.getMetrics();
        metrics.totalSize = this.getCacheSize();
        this.setMetrics(metrics);
        
        logger.debug('Cache cleanup', { 
          cleanedCount, 
          remainingSize: metrics.totalSize 
        });
      }
    } catch (cacheError) {
      logger.warn('Cache cleanup error', { error: cacheError });
    }
  }

  /**
   * Génère une clé de cache standardisée
   */
  static generateKey(type: string, identifier: string, suffix?: string): string {
    const parts = [type, identifier];
    if (suffix) {
      parts.push(suffix);
    }
    return parts.join('_');
  }

  /**
   * Détermine le TTL approprié pour une clé
   */
  private static getTtlForKey(key: string): number {
    for (const [type, ttl] of Object.entries(this.TTL_CONFIG)) {
      if (key.startsWith(type)) {
        return ttl;
      }
    }
    return this.DEFAULT_TTL;
  }

  /**
   * Éviction LRU (Least Recently Used)
   */
  private static evictLeastRecentlyUsed(): void {
    if (typeof window === 'undefined') return;
    
    try {
      let oldestKey = '';
      let oldestTime = Date.now();
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry: CacheEntry = JSON.parse(stored);
              if (entry.lastAccessed < oldestTime) {
                oldestTime = entry.lastAccessed;
                oldestKey = key;
              }
            }
          } catch {
            // Entrée corrompue, la considérer comme candidate à l'éviction
            oldestKey = key;
            oldestTime = 0;
          }
        }
      }
      
      if (oldestKey) {
        localStorage.removeItem(oldestKey);
        logger.debug('Cache eviction (LRU)', { 
          evictedKey: this.sanitizeKey(oldestKey.substring(this.CACHE_PREFIX.length)),
          age: `${Date.now() - oldestTime}ms`
        });
      }
    } catch (cacheError) {
      logger.warn('Cache eviction error', { error: cacheError });
    }
  }

  /**
   * Met à jour le taux de hit du cache
   */
  private static updateHitRate(metrics: CacheMetrics): void {
    const total = metrics.hits + metrics.misses;
    metrics.hitRate = total > 0 ? (metrics.hits / total) * 100 : 0;
  }

  /**
   * Compte le nombre d'entrées dans le cache
   */
  private static getCacheSize(): number {
    if (typeof window === 'undefined') return 0;
    
    let count = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.CACHE_PREFIX)) {
        count++;
      }
    }
    return count;
  }

  /**
   * Sanitize les clés pour les logs
   */
  private static sanitizeKey(key: string): string {
    // Masquer les tokens et IDs sensibles
    return key.replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '[UUID]')
              .replace(/campaign-\d+-[a-z0-9]+/gi, '[CAMPAIGN_TOKEN]');
  }
}

// Nettoyage automatique au chargement de la page (pas d'interval en serverless)
if (typeof window !== 'undefined') {
  // Nettoyer au chargement
  CacheService.cleanup();
  
  // Nettoyer avant fermeture de la page
  window.addEventListener('beforeunload', () => {
    CacheService.cleanup();
  });
}

